import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { PriceListPriceValue } from "../../admin/products/editProduct/updateOptionPrices";
import { useOptionPrices } from "../../admin/products/editProduct/useOptionPrices";
import type {
  UpdateOptionPurchasePriceResponse,
  UpdateOptionSellingPriceResponse,
} from "../../admin/products/productsApi";
import { updateOptionPurchasePriceForDate, updateOptionSellingPriceForDate } from "../../admin/products/productsApi";
import { isProductSellingPricesQuery } from "../../admin/products/useProductSellingPrices";
import { useProductWithId } from "../../admin/products/useProductWithId";

export const useProductOptionPrices = (productId: string, optionId: string) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { product, isLoading: isProductLoading } = useProductWithId(productId);
  const { data: optionPrices, isLoading: arePricesLoading } = useOptionPrices(productId, optionId);
  const computePricesFromPurchasePrice = optionPrices?.computePricesFromPurchasePrice ?? false;

  const onPriceListUpdated = async (optionId: string) => {
    enqueueSnackbar("Cena w cenniku została zaktualizowana", { variant: "success" });
    await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
    await queryClient.invalidateQueries({ queryKey: ["products", productId, "options", optionId, "prices"] });
  };

  const onPriceListUpdateFailed = (error: unknown) => {
    enqueueSnackbar(`Nie udało się zaktualizować cennika. Powód: ${(error as Error).message}`, {
      variant: "error",
    });
  };

  const updateOptionSellingPriceMutation = useMutation<
    UpdateOptionSellingPriceResponse,
    unknown,
    { date: string; value: PriceListPriceValue }
  >({
    mutationFn: ({ date, value }) => updateOptionSellingPriceForDate(productId, optionId, date, { value }),
    onSuccess: (data) => onPriceListUpdated(data.optionId),
    onError: onPriceListUpdateFailed,
  });

  const updateOptionPurchasePriceMutation = useMutation<
    UpdateOptionPurchasePriceResponse,
    unknown,
    { date: string; value: PriceListPriceValue }
  >({
    mutationFn: ({ date, value }) => updateOptionPurchasePriceForDate(productId, optionId, date, { value }),
    onSuccess: (data) => onPriceListUpdated(data.optionId),
    onError: onPriceListUpdateFailed,
  });

  return {
    currency: product?.currency,
    priceListPrices: optionPrices?.prices.selling,
    usePricesFrom: optionPrices?.usePricesFrom,
    useDynamicPrices: optionPrices?.useDynamicPrices,
    computePricesFromPurchasePrice,
    isLoading: isProductLoading || arePricesLoading,
    updatePriceListPrice: (date: string, price: PriceListPriceValue) =>
      computePricesFromPurchasePrice
        ? updateOptionPurchasePriceMutation.mutateAsync({ date, value: price })
        : updateOptionSellingPriceMutation.mutateAsync({ date, value: price }),
  };
};
